import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

import { AggregateOverviewFloat } from "../../components/aggregate/AggregateOverviewFloat"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

const CATEGORY = "music_detection"

/** Convert seconds → m:ss */
const fmt = (sec: number) => {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(1, "0")
  const s = Math.floor(sec % 60)
    .toString()
    .padStart(2, "0")
  return `${m}:${s}`
}

/** Subvalue item may be API shape (start_offset/end_offset) or Python shape (timestamp, value, metadata) */
interface MusicSubvalue {
  start_offset?: number
  end_offset?: number
  timestamp?: number
  value?: number
  metadata?: { start_offset?: number; end_offset?: number; speaker?: string }
}

function getSegmentBounds(item: MusicSubvalue | SubvalueByTimestamp): {
  start: number
  end: number
} {
  if (
    typeof item.start_offset === "number" &&
    typeof item.end_offset === "number"
  ) {
    return { start: item.start_offset, end: item.end_offset }
  }
  if (
    item.metadata?.start_offset != null &&
    item.metadata?.end_offset != null
  ) {
    return {
      start: item.metadata.start_offset as number,
      end: item.metadata.end_offset as number,
    }
  }
  if (typeof item.timestamp === "number" && typeof item.value === "number") {
    return { start: item.timestamp, end: item.timestamp + item.value }
  }
  return { start: 0, end: 0 }
}

export class MusicDetectionMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  aggregate_units = "segments"

  getOverviewNode(mos: RunMetricOutput[]): React.ReactNode {
    return <AggregateOverviewFloat mos={mos} units={this.aggregate_units} />
  }

  renderInDepthRunReview(metricOutputs: RunMetricOutput[]): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    return (
      <AggregateOverviewFloat
        mos={metricOutputs}
        units={this.aggregate_units}
      />
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const data = mo.result_json ?? {}
    const count = mo.float_value ?? data.count ?? 0
    // Use top-level subvalues_by_timestamp (preferred source)
    const subvalues_by_timestamp: SubvalueByTimestamp[] | MusicSubvalue[] =
      mo.subvalues_by_timestamp?.length > 0
        ? mo.subvalues_by_timestamp
        : // @deprecated fallback: result_json.subvalues_by_timestamp — remove after subvalues_by_timestamp migration is complete
          Array.isArray(data.subvalues_by_timestamp)
          ? data.subvalues_by_timestamp
          : []
    const segments = subvalues_by_timestamp.map((item) => ({
      ...getSegmentBounds(item),
      speaker: (item as MusicSubvalue).metadata?.speaker,
    }))

    const handleSeekToTimestamp = (timestamp: number) => {
      simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
        time: timestamp,
      })
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-surface-1 border border-coval-border rounded-lg p-4">
            <div className="text-sm text-coval-text-muted font-medium mb-1">
              Music segments
            </div>
            <div className="text-2xl font-bold text-coval-text-muted">
              {Number(count).toFixed(0)}
            </div>
          </div>
        </div>

        {segments.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-coval-text">
              Music segments ({segments.length})
            </h3>
            <div className="space-y-2">
              {segments.map(({ start, end, speaker }, idx) => (
                <button
                  type="button"
                  key={idx}
                  className={cn(
                    "flex w-full items-start gap-3 p-3 rounded-lg border border-transparent text-left",
                    "bg-surface-2 text-coval-text",
                    "cursor-pointer transition-colors duration-150",
                  )}
                  onClick={() => handleSeekToTimestamp(start)}
                >
                  <span className="shrink-0">
                    <span
                      className={cn(
                        "block px-2 py-1 rounded text-xs font-mono",
                        "bg-surface-2 text-coval-text",
                      )}
                    >
                      {fmt(start)} - {fmt(end)} ({(end - start).toFixed(1)}s)
                    </span>
                  </span>
                  <span className="block flex-1 min-w-0">
                    <span className="text-xs font-medium">
                      Music segment {idx + 1}
                      {speaker ? ` · ${speaker}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      const data = mo.result_json ?? {}
      // Use top-level subvalues_by_timestamp (preferred source)
      const subvalues: SubvalueByTimestamp[] | MusicSubvalue[] =
        (mo.subvalues_by_timestamp?.length ?? 0) > 0
          ? (mo.subvalues_by_timestamp ?? [])
          : // @deprecated fallback: result_json.subvalues_by_timestamp — remove after subvalues_by_timestamp migration is complete
            Array.isArray(data.subvalues_by_timestamp)
            ? data.subvalues_by_timestamp
            : []
      return subvalues.map((item, idx) => {
        const { start, end } = getSegmentBounds(item)
        return {
          id: `music:${idx}`,
          start,
          end: Math.max(end, start + 0.1),
          color: "rgba(94, 234, 212, 0.35)",
          drag: false,
          resize: false,
        }
      })
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      useRegions: true,
      buildReviewedResultJson: ({ editedCount, durationSeconds }) => ({
        count: editedCount,
        music_segments: editedCount,
        duration_seconds:
          durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
      }),
    }
  }

  renderMetricType(): React.ReactNode {
    return "Music Detection"
  }
}
